import { useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Badge,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { FilterModal } from "./FilterModal";
import {
  countActiveFilters,
  type FiltersWithPeriod,
} from "../logic/filter-logic";
import { useRegistry } from "../context/RegistryContext";
import { downloadTransactionsCsv } from "../api/queries";
import type { Sort } from "../api/types";

export interface TransactionFiltersProps {
  filters: FiltersWithPeriod;
  onFiltersChange: (newFilters: Partial<FiltersWithPeriod>) => void;
}

export const TransactionFilters = ({
  filters,
  onFiltersChange,
}: TransactionFiltersProps) => {
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { invoices } = useRegistry();

  const activeFilterCount = useMemo(
    () => countActiveFilters(filters),
    [filters],
  );

  const limitProp = filters.limit?.toString() || "10";
  const [limitInputValue, setLimitInputValue] = useState(limitProp);
  const [prevLimitProp, setPrevLimitProp] = useState(limitProp);

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await downloadTransactionsCsv(filters);
    } finally {
      setIsExporting(false);
    }
  };

  if (limitProp !== prevLimitProp) {
    setPrevLimitProp(limitProp);
    setLimitInputValue(limitProp);
  }

  const handleApplyFilters = (newFilters: FiltersWithPeriod) => {
    onFiltersChange(newFilters);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        mb: 2,
        gap: 1,
        alignItems: "center",
      }}
    >
      <Button
        variant="outlined"
        // Named outright rather than from its content: MUI keeps the last
        // count in a badge it has just hidden (for the exit animation), so
        // after Clear All the content still read "1 Filters".
        aria-label={
          activeFilterCount > 0
            ? `Filters (${activeFilterCount} active)`
            : "Filters"
        }
        startIcon={
          <Badge badgeContent={activeFilterCount} color="primary">
            <FilterListIcon />
          </Badge>
        }
        onClick={() => setFilterModalOpen(true)}
      >
        Filters
      </Button>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="sort-by-label">Sort By</InputLabel>
        <Select
          labelId="sort-by-label"
          id="sort-by-select"
          value={filters.sort || "dateDesc"}
          label="Sort By"
          onChange={(e) => onFiltersChange({ sort: e.target.value as Sort })}
        >
          <MenuItem value="dateDesc">Newest First</MenuItem>
          <MenuItem value="dateAsc">Oldest First</MenuItem>
          <MenuItem value="amountDesc">Highest Amount</MenuItem>
          <MenuItem value="amountAsc">Lowest Amount</MenuItem>
        </Select>
      </FormControl>

      <Autocomplete
        freeSolo
        disableClearable
        options={["10", "25", "50", "100"]}
        value={filters.limit?.toString() || "10"}
        inputValue={limitInputValue}
        onInputChange={(_, newInputValue) => {
          setLimitInputValue(newInputValue);
        }}
        onChange={(_, newValue) => {
          if (typeof newValue === "string") {
            const val = parseInt(newValue, 10);
            if (!isNaN(val) && val > 0) {
              onFiltersChange({ limit: val });
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Limit"
            size="small"
            sx={{ width: 100 }}
          />
        )}
      />

      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={handleExportCsv}
        disabled={isExporting}
      >
        {isExporting ? "Exporting…" : "Export CSV"}
      </Button>

      <FilterModal
        key={filterModalOpen ? "open" : "closed"}
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
        invoices={invoices?.map((i) => i.id) || []}
      />
    </Box>
  );
};
